import { isNil } from 'lodash';

import { VALID_AM_PM } from './constants';

export const checkValid12HourFormat = time =>
  isNil(time) || time.match(/^(0?[1-9]|1[0-2]):[0-5][0-9] [AaPp][Mm]$/);

export const stripTime = time => time.replace(/[^0-9a-z]/gi, '');

export const canBeCoercedInto12HourFormat = time => {
  const stripped_time = stripTime(time);

  // There is no valid time that has more than 6 characters including "am" or "pm" or 0 characters
  if (stripped_time.length > 6 || !stripped_time.length) {
    return false;
  }
  const letters = time.replace(/[^A-Za-z]/gi, '').toLowerCase();
  const numbers = time.replace(/[^0-9]/gi, '');
  const numberOfDigits = numbers.length;

  // There is no valid time that has more than 4 digits or any characters other than AaPpMm
  if (!VALID_AM_PM.includes(letters) || numberOfDigits > 4) {
    return false;
  }

  // If there are more than 2 digits, the last 2 digits indicate minutes and cannot be over 59
  if (
    numberOfDigits > 2 &&
    parseInt(numbers.slice(numberOfDigits - 2, numbers.length), 10) > 59
  ) {
    return false;

    // If there are only 2 digits, the digits must indicate hour and cannot be over 23
  } else if (numberOfDigits === 2 && parseInt(numbers, 10) > 23) {
    return false;

    // If there are only 2 digits & valid letters the digits must indicate hour
    // and cannot be over 12
  } else if (numberOfDigits === 2 && parseInt(numbers, 10) > 12 && letters) {
    return false;

    // If there are 4 digits, the first 2 indicate hour and cannot be over 23
  } else if (numberOfDigits === 4 && parseInt(numbers.slice(0, 2), 10) > 23) {
    return false;

    // If there are valid letters, then the first 2 digits indicate hour and cannot be over 12
  } else if (
    numberOfDigits === 4 &&
    parseInt(numbers.slice(0, 2), 10) > 12 &&
    letters
  ) {
    return false;

    // 0 as an hour can only possibly be 12:00 AM
  } else if (
    numberOfDigits === 1 &&
    parseInt(numbers[0], 10) === 0 &&
    letters
  ) {
    return false;
    // only letters cannot be a valid time
  } else if (!numberOfDigits && letters) {
    return false;
  }
  return true;
};

export const coerceIntoValid12HourFormat = time => {
  // Only keeping "a" or "p" and appending "m" later to avoid checking whether "a" or "am"
  const amPm = time.replace(/[^AaPp]/gi, '').toLowerCase();
  const hrMin = time.replace(/[^0-9]/gi, '');

  const firstTwoDigits = parseInt(hrMin.substr(0, 2), 10);
  const computedAmPm = firstTwoDigits < 12 ? 'am' : 'pm';
  const computedHour = hour => {
    if (hour === 0) {
      return 12;
    } else if (hour > 12) {
      return hour - 12;
    }
    return hour;
  };

  if (!amPm) {
    switch (hrMin.length) {
      case 1:
        // can be assumed to be the hour and AM
        // computing the 1 digit hour to get 12 if 0 is sent
        return `${computedHour(firstTwoDigits)}:00 am`;
      case 2:
        // indicates the hour is 24 hour format so the hour and AM/PM must be computed
        return `${computedHour(firstTwoDigits)}:00 ${computedAmPm}`;
      case 3:
        // can be assumed to be AM with the first digit being the hour
        // computing the 1 digit hour to get 12 if 0 is sent
        return `${computedHour(parseInt(hrMin[0], 10))}:${hrMin.substr(1)} am`;
      case 4:
        // 4 digits without AM/PM must compute hour and AM/PM
        return `${computedHour(firstTwoDigits)}:${hrMin.substr(
          2
        )} ${computedAmPm}`;
      default:
    }
  } else {
    switch (hrMin.length) {
      case 1:
        return `${hrMin}:00 ${amPm}m`;
      case 2:
        return `${hrMin}:00 ${amPm}m`;
      case 3:
        return `${hrMin[0]}:${hrMin.substr(1)} ${amPm}m`;
      case 4:
        return `${firstTwoDigits}:${hrMin.substr(2)} ${amPm}m`;
      default:
    }
  }
};

export const roundTime = (time, interval = 5) => {
  const [hrMin, amPm] = time.split(' ');
  const [hr, min] = hrMin.split(':');
  const minutes = parseInt(min, 10);
  const roundedMinutes = (Math.floor(minutes / interval) * interval)
    .toString()
    .padStart(2, '0');

  return `${hr}:${roundedMinutes} ${amPm.toLowerCase()}`;
};

export const convertFrom12HourFormat = time => {
  if (!time) {
    return time;
  }
  const amPm = time.split(' ')[1].toLowerCase();
  const hrMin = time.split(' ')[0];
  const hr = hrMin.split(':')[0];
  const min = hrMin.split(':')[1];
  if (amPm === 'pm') {
    const hrToInt = parseInt(hr, 10);
    const convertedHour = hrToInt === 12 ? 12 : hrToInt + 12;
    return `${convertedHour}:${min}`;
  }
  const convertedHour = hr === '12' ? '00' : hr;
  return `${convertedHour}:${min}`;
};
